import { Plus, X } from "lucide-react";
import { useFieldArray, useFormContext, type Control } from "react-hook-form";
import {
  useListInventoryItems,
  useListHsnCodes,
  useListStockLocations,
  getListStockLocationsQueryKey,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ItemCombobox } from "./item-combobox";

// Phase 2.1 polish E — card-stack layout. Each line item is its own card with
// a 2-column grid inside. Replaces the cramped horizontal table that put 9
// fields on one row. Item picker spans full width; numeric pairs sit side-by-side.

export interface LineItemRow {
  itemId: number | null;
  quantityReceived: number | string;
  // Legacy field kept for API/back-compat. The UI no longer sets it (every receipt
  // credits the full quantityReceived); the form always submits it as null.
  quantityRemaining?: number | string | null;
  quantityOrdered?: number | string | null;
  unitPriceActual: number | string;
  // 2026-05-20: gstRate (%) is the operator-entered value; gstAmount is auto-computed
  // (qty × unit price × rate/100) and is what's persisted/sent to the server.
  gstRate?: number | string | null;
  gstAmount?: number | string | null;
  lineTotal?: number | string | null;
  batchNumber?: string | null;
  expiryDate?: string | null;
  storageLocation?: string | null;
  // Phase 58 Plan 13 (LOC-01): the stock_locations row this line credits on
  // receipt. Only rendered/required when the caller opts in via
  // showLocationPicker — the PO-linked delivery form (a different consumer of
  // this table) is untouched.
  locationId?: number | null;
}

// 2026-05-21: 40% added — applies to aerated/sugary drinks (cold drinks) under the
// revised GST slab.
const GST_RATES = [0, 5, 12, 18, 28, 40] as const;

interface StockInLineItemsTableProps {
  control: Control<{ lineItems: LineItemRow[] }>;
  disabled?: boolean;
  // 2026-05-22: when true, the entered unit price already includes GST — previews split
  // out the embedded GST instead of adding it on top.
  priceInclusiveGst?: boolean;
  // 2026-05-30: opt-in "include on this invoice" checkbox per row. The caller drives
  // the checked state via a Set<index> kept in their own state — useFieldArray's
  // append/remove keep indices contiguous so a Set keyed by index is fine.
  showIncludeCheckbox?: boolean;
  excludedIndices?: Set<number>;
  onToggleIncluded?: (index: number, included: boolean) => void;
  // Phase 58 Plan 13 (LOC-01) — opt-in per-line "Receive into" location
  // select. Only the free-form Stock-In form turns this on; the PO-linked
  // delivery form (this table's other consumer) is unaffected.
  showLocationPicker?: boolean;
}

export function StockInLineItemsTable({
  control,
  disabled,
  priceInclusiveGst,
  showIncludeCheckbox,
  excludedIndices,
  onToggleIncluded,
  showLocationPicker,
}: StockInLineItemsTableProps) {
  const { fields, append, remove } = useFieldArray({ control, name: "lineItems" });
  const form = useFormContext();
  // 2026-05-22: resolve each item's HSN code + the GST it maps to. Effective GST is the
  // item's own gstRateOnSale when set, else the rate of its HSN code in the master.
  const { data: allItems = [] } = useListInventoryItems({ status: "Active" });
  const { data: hsnCodes = [] } = useListHsnCodes({ status: "Active" });
  const { data: stockLocations = [] } = useListStockLocations(undefined, {
    query: { enabled: !!showLocationPicker, queryKey: getListStockLocationsQueryKey() },
  });
  const activeStockLocations = stockLocations.filter((l) => l.isActive);
  const unassignedLocationId = stockLocations.find((l) => l.isUnassignedBucket)?.id ?? null;
  function itemInfo(itemId: number | null | undefined): { hsn: string | null; rate: number | null } | null {
    if (itemId == null) return null;
    const it = allItems.find((x) => x.id === itemId);
    if (!it) return null;
    const hsn = (it.hsnSacCode ?? "") || null;
    const own = it.gstRateOnSale != null ? Number(it.gstRateOnSale) : null;
    const mapped = hsn ? hsnCodes.find((c) => c.code === hsn) : undefined;
    const rate = own != null && own > 0 ? own : mapped != null ? Number(mapped.gstRate) : own;
    return { hsn, rate };
  }

  return (
    <div className="flex flex-col gap-3">
      {fields.length === 0 ? (
        <div className="rounded-md border border-dashed py-8 text-center text-sm text-muted-foreground">
          No line items yet — add at least one to save.
        </div>
      ) : null}
      {fields.map((field, i) => {
        const row = field as unknown as LineItemRow & { id: string };
        const showCheckbox = !!showIncludeCheckbox && onToggleIncluded != null;
        const checked = showCheckbox ? !(excludedIndices?.has(i) ?? false) : true;
        // Compute how many other lines are currently checked. The last
        // checked line can't be unticked (would submit an empty form);
        // when there's only one line in the form, the checkbox is also
        // disabled — there's nothing else to skip to.
        const checkedCount = fields.length - (excludedIndices?.size ?? 0);
        const isOnlyLine = fields.length === 1;
        const isLastChecked = checked && checkedCount <= 1;
        const checkboxDisabled = isOnlyLine || isLastChecked;
        return (
          <div
            key={field.id}
            className={`rounded-md border bg-card p-4 relative ${showCheckbox && !checked ? "opacity-60 bg-muted/30" : ""}`}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                {showCheckbox && (
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      // Guard against unchecking the last selected line —
                      // the form needs at least one to submit.
                      if (!e.target.checked && checkedCount <= 1) return;
                      onToggleIncluded!(i, e.target.checked);
                    }}
                    disabled={checkboxDisabled}
                    className="h-3.5 w-3.5 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                    aria-label="Include on this invoice"
                    title={
                      isOnlyLine
                        ? "Only line in this delivery — can't be unticked."
                        : isLastChecked
                        ? "At least one line must stay checked."
                        : "Include on this invoice. Untick to roll the line into a future receipt."
                    }
                  />
                )}
                Line {i + 1}
                {showCheckbox && !checked && (
                  <span className="rounded bg-muted px-1 py-px text-[10px] uppercase tracking-wide">
                    skipped
                  </span>
                )}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive h-7 w-7 p-0"
                onClick={() => remove(i)}
                disabled={disabled}
                type="button"
                aria-label="Remove line"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium mb-1 block">Item *</label>
                <ItemCombobox
                  value={row.itemId ?? null}
                  onChange={(id, meta) => {
                    const info = itemInfo(id);
                    const autoRate = info?.rate ?? meta?.gstRateOnSale ?? null;
                    // eslint-disable-next-line no-console
                    console.log("[stock-in gst-autofill] pick", {
                      row: i,
                      pickedItemId: id,
                      metaRate: meta?.gstRateOnSale,
                      infoRate: info?.rate,
                      autoRate,
                      formPresent: !!form,
                      currentGstBefore: form?.getValues?.(`lineItems.${i}.gstRate`),
                    });
                    form?.setValue(`lineItems.${i}.itemId`, id, { shouldDirty: true });
                    // Phase 58 Plan 13 (LOC-01): default "Receive into" to the
                    // item's usual sale location, else Unassigned — only when
                    // the operator hasn't already picked a location for this line.
                    if (showLocationPicker && !form?.getValues?.(`lineItems.${i}.locationId`)) {
                      const pickedItem = allItems.find((x) => x.id === id);
                      const defaultLoc = pickedItem?.defaultSaleLocationId ?? unassignedLocationId;
                      if (defaultLoc != null) {
                        form?.setValue(`lineItems.${i}.locationId`, defaultLoc, { shouldDirty: true });
                      }
                    }
                    if (autoRate != null) {
                      form?.setValue(`lineItems.${i}.gstRate`, Number(autoRate), {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                      // eslint-disable-next-line no-console
                      console.log("[stock-in gst-autofill] after setValue", {
                        row: i,
                        currentGstAfter: form?.getValues?.(`lineItems.${i}.gstRate`),
                      });
                    }
                  }}
                  showTier={false}
                  // RECIPE-DISPLAY-02: Stock-In receipt line-item picker — recipe items
                  // are never received; hide them at the source.
                  excludeRecipe
                />
                {(() => {
                  const info = itemInfo(row.itemId);
                  if (!info) return null;
                  const pickedItem = allItems.find((x) => x.id === row.itemId);
                  const packSize = (() => {
                    if (!pickedItem || !Array.isArray(pickedItem.specs)) return null;
                    for (const s of pickedItem.specs as Array<{ label?: unknown; value?: unknown }>) {
                      const label = String(s?.label ?? "").toLowerCase();
                      if (label === "piece" || label === "pieces") {
                        const n = Number(String(s?.value ?? "").trim());
                        if (Number.isInteger(n) && n > 1) return n;
                        return null;
                      }
                    }
                    return null;
                  })();
                  const enteredQty = Number(form?.watch(`lineItems.${i}.quantityReceived`)) || 0;
                  return (
                    <p className="text-[11px] text-muted-foreground mt-1">
                      HSN/SAC: <span className="font-mono">{info.hsn ?? "— not set —"}</span>
                      {" · "}GST {info.rate != null ? `${info.rate}%` : "—"}
                      {info.hsn == null && <span className="italic"> (set HSN on the item to map GST)</span>}
                      {packSize != null && (
                        <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-amber-700 font-medium">
                          Pack of {packSize} pieces — enter qty in packs; we'll credit {enteredQty > 0 ? enteredQty * packSize : `qty × ${packSize}`} pieces to stock
                        </span>
                      )}
                    </p>
                  );
                })()}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium mb-1 block">Qty Received *</label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0"
                    disabled={disabled}
                    className="font-mono tabular-nums"
                    {...(form ? form.register(`lineItems.${i}.quantityReceived` as const, { valueAsNumber: true }) : {})}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block">Qty Ordered</label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="optional"
                    disabled={disabled}
                    className="font-mono tabular-nums"
                    {...(form ? form.register(`lineItems.${i}.quantityOrdered` as const, { valueAsNumber: true }) : {})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium mb-1 block">
                    Unit Price (₹) * <span className="text-muted-foreground font-normal">{priceInclusiveGst ? "(incl. GST)" : "(excl. GST)"}</span>
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    disabled={disabled}
                    className="font-mono tabular-nums"
                    {...(form ? form.register(`lineItems.${i}.unitPriceActual` as const, { valueAsNumber: true }) : {})}
                  />
                  {(() => {
                    // Base (pre-GST) line total. When the entered price is GST-inclusive,
                    // the base is price ÷ (1 + rate/100); otherwise it's the price as-is.
                    const qty = Number(form?.watch(`lineItems.${i}.quantityReceived`)) || 0;
                    const price = Number(form?.watch(`lineItems.${i}.unitPriceActual`)) || 0;
                    const rate = Number(form?.watch(`lineItems.${i}.gstRate`)) || 0;
                    const base = priceInclusiveGst ? price / (1 + rate / 100) : price;
                    const total = Math.round(qty * base * 100) / 100;
                    return (
                      <p className="text-[11px] text-muted-foreground mt-1 font-mono tabular-nums">
                        Base (excl GST): ₹{total.toFixed(2)}
                      </p>
                    );
                  })()}
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block">GST %</label>
                  <select
                    disabled={disabled}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono tabular-nums ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    {...(form ? form.register(`lineItems.${i}.gstRate` as const, { valueAsNumber: true }) : {})}
                  >
                    {GST_RATES.map((r) => (
                      <option key={r} value={r}>{r}%</option>
                    ))}
                  </select>
                  {(() => {
                    // GST amount preview (authoritative value recomputed at submit).
                    // Exclusive: qty × price × rate/100. Inclusive: embedded GST = qty × (price − base).
                    const qty = Number(form?.watch(`lineItems.${i}.quantityReceived`)) || 0;
                    const price = Number(form?.watch(`lineItems.${i}.unitPriceActual`)) || 0;
                    const rate = Number(form?.watch(`lineItems.${i}.gstRate`)) || 0;
                    const base = priceInclusiveGst ? price / (1 + rate / 100) : price;
                    const amt = Math.round(qty * base * rate) / 100;
                    return (
                      <p className="text-[11px] text-muted-foreground mt-1 font-mono tabular-nums">
                        GST amount: ₹{amt.toFixed(2)}
                      </p>
                    );
                  })()}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium mb-1 block">Batch Number</label>
                  <Input
                    type="text"
                    placeholder="optional"
                    disabled={disabled}
                    {...(form ? form.register(`lineItems.${i}.batchNumber` as const) : {})}
                  />
                </div>
                {(() => {
                  // Only show Expiry Date when the selected item has the expiry flag ON
                  // (stored as shelfLifeDays > 0 on the item — see "Has expiry date" toggle
                  // on the item form). Items without expiry tracking skip this field.
                  const it = row.itemId ? allItems.find((x) => x.id === row.itemId) : null;
                  const requiresExpiry = Number(it?.shelfLifeDays ?? 0) > 0;
                  if (!requiresExpiry) return null;
                  return (
                    <div>
                      <label className="text-xs font-medium mb-1 block">Expiry Date</label>
                      <Input
                        type="date"
                        disabled={disabled}
                        {...(form ? form.register(`lineItems.${i}.expiryDate` as const) : {})}
                      />
                    </div>
                  );
                })()}
              </div>

              {showLocationPicker ? (
                <div>
                  <label className="text-xs font-medium mb-1 block">
                    Receive into <span className="text-destructive">*</span>
                  </label>
                  <Select
                    value={row.locationId != null ? String(row.locationId) : ""}
                    onValueChange={(v) => form?.setValue(`lineItems.${i}.locationId`, Number(v), { shouldDirty: true })}
                    disabled={disabled}
                  >
                    <SelectTrigger><SelectValue placeholder="Pick a location" /></SelectTrigger>
                    <SelectContent>
                      {activeStockLocations.map((l) => (
                        <SelectItem key={l.id} value={String(l.id)}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
            </div>
          </div>
        );
      })}

      {/* "+ Add line item" hidden when the table is in PO-linked mode (showIncludeCheckbox).
          In that flow the lines come from the PO; off-cart additions belong on the
          PO itself, not as substitution lines on the receipt. */}
      {!showIncludeCheckbox && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => append({ itemId: null, quantityReceived: 1, unitPriceActual: 0, gstRate: 0 })}
          disabled={disabled}
          className="self-start"
        >
          <Plus className="mr-2 h-4 w-4" /> Add line item
        </Button>
      )}
    </div>
  );
}
